import upperFirst from 'lodash/upperFirst';
import lowerFirst from 'lodash/lowerFirst';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import {
    AnalyticsProps,
    FetchApiProps,
    GetCurrencyList,
    GetLanguages,
    GetUserNotifications,
    HeaderCallbackProps,
    HeaderCoreProps,
    LayoutLoginParams,
    UserMenuViewModelApi,
} from '@agoda/layout';
import { CommonHeaderProps, LanguageApiResponse } from 'core/type/header.types';
import { HeaderEventHandlers } from 'component/common/Header/ActivitiesHeader.types';
import { AnalyticsActions } from 'core/messaging/analytics/AnalyticsActions';
import { NavigationState } from 'store/slice';
import { routeTo } from 'store/slice/navigation';
import { getLoginUrl } from 'core/utils/activityUrlGenerator';
import { MenuWindowState, setMenuState } from 'store/slice/menu';
import { getHeader, getMocks } from 'core/window/windowService';
import { OverlayType, showOverlay } from 'store/slice/overlay';
import { FeatureConfigType, HeaderExperiments } from '@agoda/layout/lib/framework/context/types';
import { navigationActionCreators } from '@cart-js/core';
import { AnyAction } from '@reduxjs/toolkit';
import { PointsMaxMenuViewModel } from '@agoda/layout/lib/framework/model/layout';
import { HeaderBehavior } from 'core/type/behaviors.types';
import { track, trackElementEvent, trackPageVisitEvent } from 'core/messaging/analytics/analytics';
import { getShoppingCartBehavior } from 'core/utils/cart';
import { isB } from 'core/window/bootstrapConfig';
import { ExperimentId } from 'core/type/ExperimentId';
import { fetchResponse } from 'store/api/apiUtils';
import { signOut } from 'store/api/externalApi';

interface WithMappedFeatureConfig extends CommonHeaderProps {
    headerFeatureConfigMappedToPascalCase: boolean;
    featureConfigsActual: FeatureConfigType;
}

/**
 * <b>Important:</b> This function is not a pure function.<br/>
 * Mobile header user PascalCase naming convention to match the features<br/>
 * Desktop header user camelCase naming convention to match the features<br/>
 * This function updates `featureConfigs` in the header object and adds duplicate values in PascalCase and camelCase naming conventions
 */
export const mapHeaderFeatureConfigToAllCases = (headerProps: CommonHeaderProps, headerBehavior: HeaderBehavior): CommonHeaderProps => {
    if (!headerProps && !headerBehavior) {
        return headerProps;
    }

    // 'headerFeatureConfigMappedToPascalCase is used as a caching check to prevent updating featureConfigs repeatedly
    if ('headerFeatureConfigMappedToPascalCase' in headerProps && (headerProps as WithMappedFeatureConfig).headerFeatureConfigMappedToPascalCase) {
        return headerProps;
    }

    const featureConfigs: FeatureConfigType & Record<string, unknown> = { ...headerProps.featureConfigs, ...headerBehavior.featureConfigs };
    const featureConfigsWithBothCase: FeatureConfigType & Record<string, unknown> = {};

    Object.entries(featureConfigs).forEach(([featureId, value]) => {
        featureConfigsWithBothCase[featureId] = value;

        const camelCaseFeatureId = lowerFirst(featureId);
        featureConfigsWithBothCase[camelCaseFeatureId] = value;

        const pascalCaseFeatureId = upperFirst(featureId);
        featureConfigsWithBothCase[pascalCaseFeatureId] = value;
    });

    (headerProps as WithMappedFeatureConfig).featureConfigsActual = featureConfigs;
    (headerProps as WithMappedFeatureConfig).headerFeatureConfigMappedToPascalCase = true;
    headerProps.featureConfigs = featureConfigsWithBothCase;
    return headerProps;
};

export const removeNotSupportedLanguages = (supportedLanguages: string[], result: LanguageApiResponse) => {
    if (result && result.body && supportedLanguages && supportedLanguages.length > 0) {
        const body = result.body;
        if (body.Languages && body.Languages.length > 0) {
            body.Languages = body.Languages.filter((l) => supportedLanguages.includes(l.CultureCode));
        }
    }
    return result;
};

const getMockResponse = (url: string) => {
    const mocks = getMocks();
    const mockHeader = getHeader();
    if (url.endsWith(UserMenuViewModelApi)) {
        return Promise.resolve({ body: (mockHeader.params.menuViewModel as unknown as { userMenuViewModel: object }).userMenuViewModel });
    }
    if (url.endsWith(GetCurrencyList)) {
        return Promise.resolve(mocks.currencies);
    }
    if (url.endsWith(GetLanguages)) {
        return Promise.resolve(mocks.languages);
    }
    if (url.endsWith(LayoutLoginParams)) {
        return Promise.resolve(mocks.layoutLoginParams);
    }
    return Promise.resolve({ body: undefined });
};

export const mapHeaderFetchApiProps = (config: BootstrapConfig): FetchApiProps => ({
    fetch: {
        get: (url: string, headers?: Record<string, string>) => {
            if (config.isMockData) {
                return getMockResponse(url);
            }
            if (url.endsWith(GetLanguages)) {
                const languages = config.supportedLocalization && config.supportedLocalization.languages;
                return fetchResponse(url, { headers }).then((result) => removeNotSupportedLanguages(languages, result as unknown as LanguageApiResponse));
            }
            return fetchResponse(url, { headers });
        },
        post: (url: string, body: Record<string, unknown>, headers?: Record<string, string>) => {
            if (config.isMockData) {
                const w = window as unknown as Record<string, unknown>;
                if (url.endsWith(GetUserNotifications())) {
                    return Promise.resolve({ body: w.MockUserNotifications });
                }
            }
            return fetchResponse(url, { headers, body: JSON.stringify(body) });
        },
    },
});

const doNothingFunc = () => {};

export const mapHeaderAnalyticsProps = (pageTypeId: number): AnalyticsProps => ({
    analytics: {
        visit: (additionProps?: object) => {
            trackPageVisitEvent(pageTypeId, { ...additionProps });
        },
        click: (elementName: string, eventVersion: number, additionProps?: object) => {
            trackElementEvent({ action_element_name: elementName, action_type: AnalyticsActions.Click, event_version: eventVersion }, pageTypeId, { ...additionProps });
        },
        leave: (additionProps?: object) => {
            track({ page_type_id: pageTypeId, action_type: AnalyticsActions.Leave, event_version: 1, ...additionProps });
        },
        custom: (actionType: string, additionProps: object) => {
            // Do not report seen event in header because
            // 1. Header is loaded before the page
            // 2. We get to know about the target page type after navigation, when the page is loaded using ActivitiesRoutes
            track({ action_type: actionType as AnalyticsActions, page_type_id: pageTypeId, event_version: 1, ...additionProps });
        },
    },
});

export const mapHeaderCallbackProps = (state: NavigationState, config: BootstrapConfig, handlers: HeaderEventHandlers): HeaderCallbackProps => ({
    callbacks: {
        onCurrencySelected: handlers.onCurrencyChange,
        onLanguageSelected: handlers.onLanguageChange,
        clearPriceFilter: doNothingFunc,
        onPriceViewSelected: doNothingFunc,
        updatePriceView: doNothingFunc,
        updateApplyGiftcard: doNothingFunc,
        setFlightsPostBookingAvailable: doNothingFunc,
        openCartSummary: () => handlers.dispatch(showOverlay(OverlayType.CartSummary)),
        onCartIngressSelected: () => handlers.dispatch(showOverlay(OverlayType.CartSummary)),
        onRedirectToCartPage: () => handlers.dispatch(navigationActionCreators.redirectToCartPage() as unknown as AnyAction),
        setMenuState: (menuState: MenuWindowState) => {
            handlers.dispatch(setMenuState(menuState));
        },
        signOut: async () => {
            await signOut(state.pageTypeId, config.whiteLabelId);
        },
        signOutSSO: async (isRedirect?: boolean) => {
            await signOut(state.pageTypeId, config.whiteLabelId);
        },
        redirect: (additionalParams?: Record<string, string>) => {
            if (config.behavior.common.useUniversalLoginForm) {
                handlers.dispatch(routeTo(getLoginUrl(config.browser.isDesktop, state, config.language.cultureCode, config)));
                return;
            }
            handlers.dispatch(routeTo(state.ssoUrl));
        },
    },
});

export const mapHeaderCoreProps = (headerProps: CommonHeaderProps, config: BootstrapConfig): HeaderCoreProps<PointsMaxMenuViewModel> => {
    return {
        whiteLabelId: config.whiteLabelId,
        languageId: config.language.languageId,
        culture: config.language.cultureCode,
        currency: config.currency.code,
        origin: config.pageConfig.origin,
        selectedLanguage: headerProps.selectedLanguage || config.language,
        selectedCurrency: headerProps.selectedCurrency || config.currency,
        user: headerProps.user,
        deviceType: config.userDevice.devicePlatformId,
        ssBehaviors: headerProps.ssBehaviors,
        pointsMax: headerProps.pointsMax,
        isRtl: config.language.isRtl,
    };
};

export const mapHeaderExperimentProps = (config: BootstrapConfig, headerProps: CommonHeaderProps): HeaderExperiments => ({
    shouldUseCartProp: getShoppingCartBehavior(config),
    showCurrencyIcon: headerProps.params?.experiments?.showCurrencyIcon,
    fixHamburgerMenuAndDropdownToBeAccessible: isB(ExperimentId.AVENGERS_1655, config.experiments),
});
